import { CalendarClock, CalendarPlus, Medal, PiggyBank, Settings2, Share2 } from 'lucide-react';
import { useT } from '../hooks/useT';
import { euro, type Badge, type KaartInfo, type Voortgang } from '../utils/museum';
import { datumKort } from '../utils/datum';
import type { KaartStaat } from '../utils/kaart';
import { chip } from '../utils/ui';

interface VoortgangSchermProps {
  voortgang: Voortgang;
  badges: Badge[];
  kaart: KaartInfo;
  staat: KaartStaat;
  /** Aantal musea op je wensenlijst die je nog niet bezocht hebt. */
  wensNog: number;
  onDeel: () => void;
  onAgenda: () => void;
  onKaartgegevens: () => void;
}

const vlak = 'rounded-3xl bg-slate-50/80 p-5 backdrop-blur-md dark:bg-slate-900/70';

/** Eén staaf: één tint (groen = bezocht), ronde uiteinden, track in neutraal. */
function Balk({ deel, label }: { deel: number; label: string }) {
  const pct = Math.round(Math.min(1, Math.max(0, deel)) * 100);
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      className="h-2.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10"
    >
      <div className="h-full rounded-full bg-emerald-700 transition-[width] duration-500 dark:bg-emerald-500" style={{ width: `${pct}%` }} />
    </div>
  );
}

export function VoortgangScherm({ voortgang: v, badges, kaart, staat, wensNog, onDeel, onAgenda, onKaartgegevens }: VoortgangSchermProps) {
  const { t, tf, language } = useT();
  const behaald = badges.filter((b) => b.behaald).length;
  const factor = kaart.prijs > 0 ? v.bespaard / kaart.prijs : 0;

  return (
    <div className="space-y-4">
      <h2 className="sr-only">{t('voortgangTitel')}</h2>

      <section className={vlak} aria-labelledby="vg-totaal">
        <p className="flex items-baseline gap-2">
          <span className="text-5xl font-bold tabular-nums">{v.bezocht}</span>
          <span id="vg-totaal" className="text-slate-600 dark:text-slate-400">
            {tf('bezochtVanKort', { totaal: v.totaal })}
          </span>
        </p>
        <div className="mt-3">
          <Balk deel={v.totaal ? v.bezocht / v.totaal : 0} label={tf('bezochtVan', { n: v.bezocht, totaal: v.totaal })} />
        </div>
        <dl className="mt-5 grid grid-cols-3 divide-x divide-black/5 text-center dark:divide-white/10">
          {[
            [t('statBezoeken'), v.bezoeken],
            [t('statWensen'), v.wensen],
            [t('statFavorieten'), v.favorieten],
          ].map(([label, n]) => (
            <div key={label} className="px-1">
              <dd className="text-2xl font-bold tabular-nums">{n}</dd>
              <dt className="text-xs text-slate-600 dark:text-slate-400">{label}</dt>
            </div>
          ))}
        </dl>
      </section>

      <section className={`${vlak} bg-emerald-100/80 text-emerald-950 dark:bg-emerald-950/60 dark:text-emerald-50`} aria-labelledby="vg-terug">
        <h3 id="vg-terug" className="flex items-center gap-2 font-semibold">
          <PiggyBank size={20} aria-hidden /> {t('terugTitel')}
        </h3>
        <p className="mt-2 flex flex-wrap items-baseline gap-x-2">
          <span className="text-4xl font-bold tabular-nums">{euro(v.bespaard, language)}</span>
          <span className="opacity-80">{t('terugLabel')}</span>
        </p>
        <div className="mt-3">
          <Balk deel={factor} label={t('terugTitel')} />
        </div>
        <p className="mt-3 font-semibold">
          {factor >= 1
            ? tf('terugWinst', { x: factor.toLocaleString(language === 'en' ? 'en-GB' : 'nl-NL', { maximumFractionDigits: 1 }) })
            : tf('terugNog', { bedrag: euro(kaart.prijs - v.bespaard, language) })}
        </p>
        <p className="mt-1 text-sm opacity-80">
          {kaart.vanaf
            ? tf('terugKaart', { prijs: euro(kaart.prijs, language), datum: datumKort(kaart.vanaf, language) })
            : tf('terugKaartZonder', { prijs: euro(kaart.prijs, language) })}
          {v.zonderPrijs > 0 && <> {tf('terugZonderPrijs', { n: v.zonderPrijs })}</>}
        </p>
      </section>

      <section className={vlak} aria-labelledby="vg-kaart">
        <h3 id="vg-kaart" className="flex items-center gap-2 font-semibold">
          <CalendarClock size={20} aria-hidden /> {t('kaartTitel')}
        </h3>
        {staat.soort === 'onbekend' ? (
          <p className="mt-2 text-slate-600 dark:text-slate-400">{t('kaartZonderDatum')}</p>
        ) : (
          <>
            <p className="mt-2 text-2xl font-bold">
              {staat.soort === 'verlopen'
                ? tf('kaartVerlopenOp', { datum: datumKort(staat.verval, language) })
                : tf('kaartGeldigTot', { datum: datumKort(staat.verval, language) })}
            </p>
            <p className={`mt-1 font-semibold ${staat.soort === 'verlopen' || staat.binnenkort ? 'text-amber-800 dark:text-amber-300' : ''}`}>
              {staat.soort === 'verlopen'
                ? tf('herinneringVerlopen', { datum: datumKort(staat.verval, language) })
                : staat.dagen === 0
                  ? t('kaartLaatsteDag')
                  : staat.dagen === 1
                    ? t('kaartNogEenDag')
                    : tf('kaartNogDagen', { n: staat.dagen })}
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {wensNog > 0 ? tf('kaartWensNog', { n: wensNog }) : t('kaartWensGeen')}
            </p>
          </>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          {staat.soort === 'geldig' && (
            <button onClick={onAgenda} className={chip(false)}>
              <CalendarPlus size={18} aria-hidden /> {t('kaartAgendaKnop')}
            </button>
          )}
          <button onClick={onKaartgegevens} className={chip(false)}>
            <Settings2 size={18} aria-hidden /> {t(staat.soort === 'onbekend' ? 'kaartGegevensKnop' : 'kaartGegevensWijzig')}
          </button>
        </div>
      </section>

      <section className={vlak} aria-labelledby="vg-prov">
        <h3 id="vg-prov" className="mb-4 font-semibold">{t('provinciesTitel')}</h3>
        <ul className="space-y-4">
          {v.perProvincie.map((p) => (
            <li key={p.provincie} className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="font-semibold">{p.provincie}</span>
                <span className="tabular-nums text-slate-600 dark:text-slate-400">{tf('provincieRij', { n: p.bezocht, totaal: p.totaal })}</span>
              </div>
              <Balk deel={p.bezocht / p.totaal} label={`${p.provincie}: ${tf('provincieRij', { n: p.bezocht, totaal: p.totaal })}`} />
            </li>
          ))}
        </ul>
      </section>

      <section className={vlak} aria-labelledby="vg-badges">
        <div className="mb-4 flex items-baseline justify-between gap-3">
          <h3 id="vg-badges" className="font-semibold">{t('badgesTitel')}</h3>
          <span className="text-sm text-slate-600 dark:text-slate-400">{tf('badgesAantal', { n: behaald, totaal: badges.length })}</span>
        </div>
        <ul className="grid grid-cols-2 gap-x-3 gap-y-5 sm:grid-cols-3">
          {badges.map((b) => (
            <li key={b.id} className="flex items-start gap-3">
              <span
                className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${
                  b.behaald ? 'bg-amber-200 text-amber-900 dark:bg-amber-900/70 dark:text-amber-200' : 'bg-black/5 text-slate-500 dark:bg-white/5 dark:text-slate-500'
                }`}
              >
                <Medal size={22} aria-hidden />
              </span>
              <span className="min-w-0">
                <span className={`block text-sm font-semibold ${b.behaald ? '' : 'text-slate-600 dark:text-slate-400'}`}>
                  {t(`badge_${b.id}`)}
                  <span className="sr-only">{b.behaald ? ' ✓' : ` (${Math.round(b.voortgang * 100)}%)`}</span>
                </span>
                <span className="block text-xs text-slate-600 dark:text-slate-400">{t(`badge_${b.id}_uitleg`)}</span>
                {!b.behaald && b.voortgang > 0 && (
                  <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-black/10 dark:bg-white/10" aria-hidden>
                    <span className="block h-full rounded-full bg-amber-600" style={{ width: `${Math.round(b.voortgang * 100)}%` }} />
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <button
        onClick={onDeel}
        className="flex min-h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-slate-900 px-5 font-semibold text-slate-50 transition-all duration-150 active:scale-95 dark:bg-slate-100 dark:text-slate-900"
      >
        <Share2 size={18} aria-hidden /> {t('deelWens')}
      </button>
    </div>
  );
}
